import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Check as CheckIcon } from "@mui/icons-material";

import {
  IdsExpandMore as ArrowDropDownIcon,
  IdsExpandLess as ArrowDropUpIcon,
} from "components/shared/svgIcons";
import { Hero2Text } from "components/shared/Typography";
import { Menu, MenuItem } from "components/shared";
import { ASSIGNED_USERS } from "helpers/apis";
import useAxios from "helpers/axios";
import { colors } from "theme";
import { IAssignedUser, UserStatus } from "types/user";

const useStyles = makeStyles((theme: Theme) => ({
  userId: {
    fontSize: 12,
    lineHeight: "17.5px",
    letterSpacing: 0.5,
    color: colors.grey[300],
  },
  menu: {
    width: 136,
    "& li": {
      borderBottom: `1px solid ${colors.grey[400]}`,
    },
  },
  statusLabel: {
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 400,
    letterSpacing: 0.5,
    marginRight: 28,
    marginLeft: 6,
  },
  statusBtn: {
    color: "white",
    "&.inactive": {
      background: colors.grey[300],
      color: colors.semantic.neutral,
    },
    "&.verified": {
      background: colors.ocean[600],
    },
    "&.pending": {
      background: colors.semantic.warning,
    },
    "&.decline": {
      background: colors.nero[300],
    },
  },
}));

const StatusButton = ({ userInfo }: { userInfo: IAssignedUser }) => {
  const classes = useStyles();
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [status, setStatus] = useState<UserStatus>(userInfo.status);
  const [showMark, setShowMark] = useState<boolean>(false);
  const { fetchData } = useAxios({
    config: { url: `${ASSIGNED_USERS}${userInfo.id}/`, method: "put" },
    callOnInit: false,
  });

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onStatusChange = (event: any) => {
    setAnchorEl(null);
    if (event.target?.value >= 0) {
      fetchData(
        {
          data: {
            status: event.target.value,
          },
        },
        () => {
          setStatus(event.target.value);
          setShowMark(true);
          setTimeout(() => {
            setShowMark(false);
          }, 5000);
        }
      );
    }
  };

  return (
    <Box display="flex" alignItems="center" width={xs ? 1 : "auto"}>
      {showMark && <CheckIcon color="secondary" />}
      <Typography className={classes.statusLabel}>Status</Typography>
      <Button
        variant="contained"
        endIcon={
          <Box width={22} height={22}>
            {Boolean(anchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </Box>
        }
        size="small"
        onClick={openMenu}
        fullWidth={xs}
        className={`${UserStatus[status].toLowerCase()} ${classes.statusBtn}`}
      >
        {UserStatus[status]}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={onStatusChange}
        classes={{ paper: classes.menu }}
      >
        <MenuItem key={UserStatus.Pending} size="sm" value={UserStatus.Pending}>
          PENDING
        </MenuItem>
        <MenuItem
          key={UserStatus.Verified}
          size="sm"
          value={UserStatus.Verified}
        >
          VERIFIED
        </MenuItem>
        <MenuItem key={UserStatus.Decline} size="sm" value={UserStatus.Decline}>
          DECLINE
        </MenuItem>
        <MenuItem
          key={UserStatus.Inactive}
          size="sm"
          value={UserStatus.Inactive}
        >
          INACTIVE
        </MenuItem>
      </Menu>
    </Box>
  );
};

export const UserStatusPanel = ({ userInfo }: { userInfo: IAssignedUser }) => {
  const classes = useStyles();

  return (
    <Box
      bgcolor="white"
      p={3}
      pt={2}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexDirection={{ xs: "column", sm: "row" }}
      gap={{ xs: "16px", sm: "25px" }}
    >
      <Grid container direction="column">
        <Typography className={classes.userId}>
          User ID: {userInfo.id}
        </Typography>
        <Hero2Text>
          {userInfo.firstName} {userInfo.lastName}
        </Hero2Text>
      </Grid>

      <StatusButton userInfo={userInfo} />
    </Box>
  );
};
